import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {
  useCreateGoogleTenantMailboxMutation,
  useCreateGoogleUserMailboxMutation,
  useCreateMicrosoftTenantMailboxMutation,
  useCreateMicrosoftUserMailboxMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {buildUrl, userMailboxOAuthFinish} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {MailboxScope} from '../types/MailboxScope';

const ConnectOAuthMailboxFormSchema = z.object({
  name: z.string().min(1, i18n.t('general.validations.fieldIsRequired')).max(255),
});

type ConnectOAuthMailboxFormValues = z.infer<typeof ConnectOAuthMailboxFormSchema>;

interface ConnectOAuthMailboxFormProps {
  provider: 'google' | 'microsoft';
  scope: MailboxScope;
}

export function ConnectOAuthMailboxForm(props: ConnectOAuthMailboxFormProps) {
  const [createGoogleUserMailbox] = useCreateGoogleUserMailboxMutation();
  const [createMicrosoftUserMailbox] = useCreateMicrosoftUserMailboxMutation();
  const [createGoogleTenantMailbox] = useCreateGoogleTenantMailboxMutation();
  const [createMicrosoftTenantMailbox] = useCreateMicrosoftTenantMailboxMutation();

  const handleSubmit: FormSubmitHandler<ConnectOAuthMailboxFormValues> = async (values) => {
    const redirectUri = buildUrl(userMailboxOAuthFinish, undefined, false);
    const requestBody = {name: values.name, redirectUri};

    const startOAuth = match({provider: props.provider, scope: props.scope})
      .with({provider: 'google', scope: 'user'}, () =>
        createGoogleUserMailbox({createUserGoogleMailboxRequestBody: requestBody})
      )
      .with({provider: 'microsoft', scope: 'user'}, () =>
        createMicrosoftUserMailbox({createUserMicrosoftMailboxRequestBody: requestBody})
      )
      .with({provider: 'google', scope: 'tenant'}, () =>
        createGoogleTenantMailbox({createTenantGoogleMailboxRequestBody: requestBody})
      )
      .with({provider: 'microsoft', scope: 'tenant'}, () =>
        createMicrosoftTenantMailbox({createTenantMicrosoftMailboxRequestBody: requestBody})
      )
      .exhaustive();

    await startOAuth
      .unwrap()
      .then(({oauthUrl}) => {
        window.location.href = oauthUrl;
      })
      .catch(handleApiError);
  };

  return (
    <Form<ConnectOAuthMailboxFormValues>
      onSubmit={handleSubmit}
      experimentalZodSchema={ConnectOAuthMailboxFormSchema}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            name="name"
            type="text"
            label={i18n.t('entity.mailbox.labels.name')}
            tooltip={i18n.t('entity.mailbox.labels.nameTooltip')}
            control={control}
            isRequired
          />
          <Separator />
          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.cancel')}
              variant="secondary"
              onClick={closeCurrentDialog}
            />
            <FormButton
              control={control}
              type="submit"
              title={i18n.t('page.myProfile.receivingMailbox.continueToProvider')}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
